import { useQueryClient } from "@tanstack/react-query";
import { journalsQueries } from "@entities/journal";
import { $journalApi } from "@entities/journal/fetch/fetch-client.ts";

type Developer = "developer";

type Participants =
  | Developer
  | "technicalCustomer"
  | "projectDocumentationContractor"
  | "buildingContractor";

type MultipleParticipants = "designerSupervisions" | "otherDevelopers";

export function useCreateParticipant({
  titleId,
  isParticipant,
}: {
  titleId: string;
  isParticipant?: boolean;
}) {
  const queryClient = useQueryClient();
  const mutation = $journalApi.useMutation(
    "post",
    "/api/v1/journals/title/{titleId}/companiesParticipants",
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: journalsQueries.getTitleJournal({ titleId }).queryKey,
        });
      },
    },
  );

  return {
    ...mutation,
    mutate: <T extends Participants | MultipleParticipants>(params: {
      projectParticipantId: T extends MultipleParticipants ? string[] : string;
      developerType?: T extends Developer
        ? "developer" | "REGIONAL" | "OPERATING"
        : undefined;
      companyId: T extends MultipleParticipants ? string[] : string;
      participantType: T;
      mutateOptions?: {
        onSuccess?: () => void;
        onError?: () => void;
        onSettled?: () => void;
      };
    }) => {
      const existingData =
        queryClient.getQueryData<any>(
          journalsQueries.getTitleJournal({ titleId }).queryKey,
        )?.mainJournal?.titleJournal?.[params.participantType] || [];

      const newParticipant = Array.isArray(params.projectParticipantId)
        ? params.projectParticipantId.map((id, index) => ({
            projectParticipantId: id,
            companyId: params.companyId[index],
            representatives: [],
          }))
        : {
            developerType: params.developerType,
            projectParticipantId: params.projectParticipantId,
            companyId: params.companyId,
            representatives: [],
          };

      let updatedData;
      if (
        (params.participantType === "designerSupervisions" ||
          params.participantType === "otherDevelopers") &&
        isParticipant
      ) {
        updatedData = [...existingData, newParticipant];
      } else {
        updatedData = newParticipant;
      }

      const body = {
        [params.participantType]: updatedData,
      };

      return mutation.mutate(
        {
          params: {
            path: {
              titleId,
            },
          },
          body,
        },
        params.mutateOptions,
      );
    },
  };
}
